import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import ErrorBoundary from './components/ErrorBoundary';
import { LoadingFallback } from './components/LoadingFallback';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';

// Lazy load components
const HomePage = React.lazy(() => import('./components/HomePage'));
const BlogList = React.lazy(() => import('./components/BlogList'));
const BlogPost = React.lazy(() => import('./components/BlogPost'));
const AdminRoutes = React.lazy(() => import('./components/AdminRoutes'));
const PrivateRoute = React.lazy(() => import('./components/PrivateRoute'));
const Login = React.lazy(() => import('./components/Login'));
const InitialSetup = React.lazy(() => import('./components/InitialSetup'));
const ExitIntentPopup = React.lazy(() => import('./components/ExitIntentPopup'));
const TemplatesPage = React.lazy(() => import('./components/templates'));
const PatientEducationTemplate = React.lazy(() => import('./components/templates/PatientEducationTemplate'));
const ContentCalendarTemplate = React.lazy(() => import('./components/templates/ContentCalendarTemplate'));
const ResourcesPage = React.lazy(() => import('./components/resources'));
const ContactConfirmation = React.lazy(() => import('./components/ContactConfirmation'));
const ContactError = React.lazy(() => import('./components/ContactError'));
const TestFormPage = React.lazy(() => import('./components/TestFormPage'));
const TestFormSubmissions = React.lazy(() => import('./components/TestFormSubmissions'));
const ConfirmPostPage = React.lazy(() => import('./components/ConfirmPostPage'));
const Privacy = React.lazy(() => import('./pages/Privacy'));
const PortfolioPage = React.lazy(() => import('./components/PortfolioPage'));
const PlatformSpecIframe = React.lazy(() => import('./components/PlatformSpecIframe'));
const ShortUrlRedirect = React.lazy(() => import('./components/ShortUrlRedirect'));

import { useExitIntent } from './hooks/useExitIntent';
import { useUserBehavior } from './hooks/useUserBehavior';

function App() {
  const { shouldShow, reset } = useExitIntent({ threshold: 20, maxDisplays: 2 });
  const { behavior, updateBehavior } = useUserBehavior();

  const getPopupType = () => {
    if (behavior.hasCompletedForm) {
      return 'newsletter';
    }
    if (behavior.hasStartedForm || behavior.timeOnSite > 30) {
      return 'contact';
    }
    return 'quick-download';
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <HelmetProvider>
      <ErrorBoundary>
        <Helmet>
          <title>Turnkey Business Platform</title>
          <meta name="description" content="A comprehensive business platform with CRM, content management, multilingual support, and more." />
          <meta property="og:title" content="Turnkey Business Platform" />
          <meta property="og:description" content="A comprehensive business platform with CRM, content management, multilingual support, and more." />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={window.location.href} />
          <link rel="preconnect" href="https://images.unsplash.com" />
          <link rel="dns-prefetch" href="https://images.unsplash.com" />
        </Helmet>

        <Router>
          <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col">
            <Navigation scrollToSection={scrollToSection} />
            <main className="flex-grow w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route 
                    path="/" 
                    element={
                      <HomePage 
                        scrollToSection={scrollToSection}
                        onFormStart={() => updateBehavior({ hasStartedForm: true })}
                        onFormComplete={() => updateBehavior({ hasCompletedForm: true })}
                      />
                    } 
                  />
                  <Route 
                    path="/blog" 
                    element={
                      <BlogList 
                        onVisit={() => updateBehavior({ hasVisitedBlog: true })}
                      />
                    } 
                  />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/setup" element={<InitialSetup />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/templates" element={<TemplatesPage />} />
                  <Route path="/templates/patient-education" element={<PatientEducationTemplate />} />
                  <Route path="/templates/content-calendar" element={<ContentCalendarTemplate />} />
                  <Route path="/resources" element={<ResourcesPage />} />
                  <Route path="/contact/confirmation" element={<ContactConfirmation />} />
                  <Route path="/contact/error" element={<ContactError />} />
                  <Route path="/test-form" element={<TestFormPage />} />
                  <Route path="/test-submissions" element={<TestFormSubmissions />} />
                  <Route path="/confirm-post/:token" element={<ConfirmPostPage />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/portfolio" element={<PortfolioPage />} />
                  <Route path="/portfolio/platform-spec" element={<PlatformSpecIframe />} />
                  <Route path="/s/:shortCode" element={<ShortUrlRedirect />} />
                  <Route
                    path="/admin/*"
                    element={
                      <PrivateRoute requiredRole="admin">
                        <AdminRoutes />
                      </PrivateRoute>
                    }
                  />
                </Routes>
              </Suspense>
            </main>
            <Footer />

            <Suspense fallback={null}>
              <ExitIntentPopup
                isOpen={shouldShow}
                onClose={reset}
                type={getPopupType()}
              />
            </Suspense>
            
            <CookieConsent />
          </div>
        </Router>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;